import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { updateUserInfo } from '../../store/actions/OnboardingActions';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import { getAuth0Email } from '../../auth0-integration/utils/auth0EmailUtils';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const UserOnboardingStep: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user: auth0User } = useAuth0();
  const { t } = useTranslation();
  const onboarding = useSelector((state: RootState) => state.onboarding);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: onboarding.user?.role || 'full_access',
    email: '',
  });



  const realEmail = getAuth0Email(auth0User);

  // Auto-populate email from Auth0 when component mounts or realEmail changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (realEmail && !formData.email) {
      setFormData((prev) => ({
        ...prev,
        email: realEmail,
      }));
    }
  }, [realEmail]);



  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      return;
    }

    // Use pre-assigned role if available, otherwise use selected role
    const roleToSubmit = onboarding.user?.role || formData.role;

    if (!roleToSubmit) {
      return;
    }

    try {
      await dispatch(
        updateUserInfo({
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          role: roleToSubmit,
          email: formData.email.trim(),
        }) as any
      );
    } catch (error) {
      console.error('Error updating user info:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="w-full max-w-2xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-light dark:shadow-dark p-6">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">{t('Complete your profile')}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('Please enter your personal information to set up your account.')}
        </p>

        {realEmail && (
          <div className="mb-6">
            <label className="block text-xs font-medium text-gray-900 dark:text-white mb-2">
              {t('Email')}
            </label>
            <input
              type="email"
              value={formData.email}
              disabled
              className="w-full px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-sm text-gray-900 dark:text-white cursor-not-allowed opacity-75"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center gap-1">
              <CheckCircleIcon className="w-3 h-3 text-success-500" />
              {t('Email verified and secured')}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <Input
              type="text"
              label={t('First Name')}
              placeholder={t('Enter your first name')}
              value={formData.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              required
            />
          </div>
          <div>
            <Input
              type="text"
              label={t('Last Name')}
              placeholder={t('Enter your last name')}
              value={formData.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              required
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            size="small"
            disabled={
              !formData.firstName.trim() ||
              !formData.lastName.trim() ||
              onboarding.isLoading
            }
            isLoading={onboarding.isLoading}
            className="w-full md:w-auto"
          >
            {t('Continue')}
          </Button>
        </div>
      </div>

    </form>
  );
};

export default UserOnboardingStep;
